import type { Answers, CurriculumItem, CurriculumTopic, ProposalType } from './types'

// 提案する研修回数の下限。根拠は助成金の訓練時間下限10時間（1回2時間×5回）。§6.2
export const MIN_SESSIONS = 5

// BizCurriculumTopic の url_name をキーにした講座名フォールバック（DBが未ロードのテスト環境用）。
const COURSE_TITLES: Record<string, string> = {
  listing: 'リスティング広告運用',
  display: 'ディスプレイ広告運用',
  meta: 'Meta広告運用',
  instagram: 'Instagram運用',
  seo: 'SEO・コンテンツマーケティング',
  ga4: 'GA4基礎',
  'ga4-advanced': 'GA4応用',
  'marketing-overview': 'マーケティング全体像',
  'digital-marketing-strategy': 'デジタルマーケティング戦略',
  'canva-ai': 'Canva×AI',
  aio: 'AIO',
  branding: 'ブランディング',
  'influencer-marketing': 'インフルエンサーマーケティング',
  meo: 'MEO',
}

const COURSE_AIMS: Record<string, string> = {
  listing: '実機操作で入稿から改善までを習得し、判断の型をつくる',
  display: '検討層への配信設計とクリエイティブ改善の型を身につける',
  meta: 'Meta広告の配信設計とクリエイティブ改善の型を身につける',
  instagram: 'アカウント設計・投稿企画・分析までの一連の型をつくる',
  seo: '記事設計から検索意図の分析までの一連の型をつくる',
  ga4: '数値を自分で取りに行き、原因の仮説まで立てられる状態にする',
  'ga4-advanced': '仮説検証のサイクルを自走できる状態にする',
  'marketing-overview': '全体像とKPI設計の共通言語をつくる',
  'digital-marketing-strategy': '戦略設計からチャネル選定までの考え方を身につける',
  'canva-ai': '生成AIを使った制作物のスピードと質を上げる',
  aio: '生成AI検索時代のコンテンツ設計を理解する',
  branding: 'ブランドの一貫性を保つための考え方を身につける',
  'influencer-marketing': '起用の判断軸と効果測定の型をつくる',
  meo: '実店舗への集客経路をもう一つ持つ',
}

function courseTitle(topics: CurriculumTopic[], urlName: string): string {
  return topics.find((t) => t.url_name === urlName)?.title ?? COURSE_TITLES[urlName] ?? urlName
}

function courseAim(urlName: string): string {
  return COURSE_AIMS[urlName] ?? ''
}

interface CourseRef {
  urlName: string
  count: number
}

// Q5（苦手領域）1件を、割り当てる講座（複数のことがある）に展開する。§6.1
function expandQ5Item(value: string, a: Answers, forHeavyGa4: boolean): CourseRef[] {
  switch (value) {
    case 'q5_ad':
      return [{ urlName: 'listing', count: 3 }]
    case 'q5_creative':
      return [
        { urlName: 'display', count: 1 },
        { urlName: 'meta', count: 1 },
      ]
    case 'q5_sns':
      // Q4で実施中の媒体を優先する想定。現状Q4に媒体別の選択肢が無いため、既定でInstagramを割り当てる。
      return [{ urlName: 'instagram', count: 2 }]
    case 'q5_seo':
      return [{ urlName: 'seo', count: 3 }]
    case 'q5_ga4':
      return forHeavyGa4
        ? [
            { urlName: 'ga4', count: 2 },
            { urlName: 'ga4-advanced', count: 2 },
          ]
        : [{ urlName: 'ga4', count: 2 }]
    case 'q5_strategy':
      return [
        { urlName: 'marketing-overview', count: 1 },
        { urlName: 'digital-marketing-strategy', count: 1 },
      ]
    case 'q5_ai':
      return a.Q5.includes('q5_seo')
        ? [
            { urlName: 'canva-ai', count: 3 },
            { urlName: 'aio', count: 1 },
          ]
        : [{ urlName: 'canva-ai', count: 3 }]
    default:
      return []
  }
}

// Q5選択肢の宣言順（実務直結度順の代理）で、実務講座（戦略・GA4を除く）を展開する。
const PRACTICAL_ORDER = ['q5_ad', 'q5_creative', 'q5_sns', 'q5_seo', 'q5_ai']

function practicalCourses(a: Answers, forHeavyGa4: boolean, excludeStrategyAndGa4: boolean): CourseRef[] {
  const values = excludeStrategyAndGa4 ? a.Q5.filter((v) => PRACTICAL_ORDER.includes(v)) : a.Q5
  const ordered = [...values].sort((x, y) => PRACTICAL_ORDER.indexOf(x) - PRACTICAL_ORDER.indexOf(y))
  return ordered.flatMap((v) => expandQ5Item(v, a, forHeavyGa4))
}

// Q4の値→対応する講座。LP改善・メール/MAは在庫に該当講座が無いため対象外。
const Q4_COURSE_MAP: Record<string, CourseRef> = {
  q4_search: { urlName: 'listing', count: 3 },
  q4_snsad: { urlName: 'meta', count: 1 },
  q4_snsorg: { urlName: 'instagram', count: 2 },
  q4_seo: { urlName: 'seo', count: 3 },
}

// §6.2.1 補完順。順1〜4は複数回の講座、順5は1回ずつの端数調整。
function fillOrderCandidates(a: Answers, existing: Set<string>): CourseRef[] {
  const candidates: CourseRef[] = []

  // 順1: Q4で実施中の施策に対応する講義
  for (const v of a.Q4) {
    const c = Q4_COURSE_MAP[v]
    if (c && !existing.has(c.urlName)) candidates.push(c)
  }

  // 順2: GA4基礎（既に入っていればGA4応用）
  if (existing.has('ga4')) {
    if (!existing.has('ga4-advanced')) candidates.push({ urlName: 'ga4-advanced', count: 2 })
  } else {
    candidates.push({ urlName: 'ga4', count: 2 })
  }

  // 順3: マーケティング全体像＋デジタルマーケティング戦略
  if (!existing.has('marketing-overview') && !existing.has('digital-marketing-strategy')) {
    candidates.push({ urlName: 'marketing-overview', count: 1 })
    candidates.push({ urlName: 'digital-marketing-strategy', count: 1 })
  }

  // 順4: Canva×AI（Q5にSEOがあればAIOを先に）
  if (!existing.has('canva-ai')) {
    if (a.Q5.includes('q5_seo') && !existing.has('aio')) {
      candidates.push({ urlName: 'aio', count: 1 })
    }
    candidates.push({ urlName: 'canva-ai', count: 3 })
  }

  return candidates
}

// 順5: 端数調整（各1回）
function singleFillCandidates(existing: Set<string>): CourseRef[] {
  return ['aio', 'branding', 'influencer-marketing', 'meo']
    .filter((u) => !existing.has(u))
    .map((urlName) => ({ urlName, count: 1 }))
}

function sumRefs(refs: CourseRef[]): number {
  return refs.reduce((sum, r) => sum + r.count, 0)
}

// §6.2.1: 5回に満たない場合、上から順に足し、5に達した時点で止める。
// 残りが1〜2回になった時点で複数回講座は諦め、1回ずつの端数調整（順5）に切り替える。
// 講義を途中で切って回数を合わせることはしない（5ちょうどに収まらない場合は超過側に倒す）。
function padToMinimum(refs: CourseRef[], a: Answers, min: number = MIN_SESSIONS): CourseRef[] {
  const result = [...refs]
  const existing = new Set(result.map((r) => r.urlName))

  for (const candidate of fillOrderCandidates(a, existing)) {
    if (sumRefs(result) >= min) break
    if (min - sumRefs(result) <= 2) break
    result.push(candidate)
    existing.add(candidate.urlName)
  }

  const singles = singleFillCandidates(existing)
  let i = 0
  while (sumRefs(result) < min) {
    const next = singles[i] ?? singles[singles.length - 1]
    result.push(next)
    i += 1
    if (i > singles.length + 2) break // 保険。在庫が尽きても無限ループしない
  }

  return result
}

function toItems(refs: CourseRef[], topics: CurriculumTopic[], startOrder: number): CurriculumItem[] {
  return refs.map((ref, i) => ({
    order: startOrder + i,
    name: courseTitle(topics, ref.urlName),
    aim: courseAim(ref.urlName),
    count: ref.count,
    urlName: ref.urlName,
  }))
}

function buildTypeA(a: Answers, topics: CurriculumTopic[]): CurriculumItem[] {
  if (a.Q5.includes('q5_all')) {
    return buildTypeB(a, topics)
  }
  const refs = padToMinimum(practicalCourses(a, false, false), a)
  return toItems(refs, topics, 1)
}

function buildTypeB(a: Answers, topics: CurriculumTopic[]): CurriculumItem[] {
  const includeGa4Advanced = a.Q5.includes('q5_ga4')
  const middleRefs = practicalCourses(a, false, true)
  const middle = middleRefs.length > 0 ? middleRefs : [{ urlName: 'listing', count: 3 }]

  const items: CurriculumItem[] = []
  items.push({
    order: 1,
    name: `${courseTitle(topics, 'marketing-overview')}／${courseTitle(topics, 'digital-marketing-strategy')}`,
    aim: courseAim('marketing-overview'),
    count: 2,
  })
  items.push(...toItems(middle, topics, 2))
  items.push({
    order: items.length + 1,
    name: includeGa4Advanced
      ? `${courseTitle(topics, 'ga4')}／${courseTitle(topics, 'ga4-advanced')}`
      : courseTitle(topics, 'ga4'),
    aim: courseAim('ga4'),
    count: includeGa4Advanced ? 4 : 2,
  })
  return items
}

function inhouseSupportName(a: Answers): string {
  const hasAd = a.Q4.some((v) => v === 'q4_search' || v === 'q4_snsad')
  const hasSnsOnly = a.Q4.includes('q4_snsorg') && !hasAd
  return hasSnsOnly ? 'SNS運用インハウスサポート' : '広告運用インハウスサポート'
}

function buildTypeC(a: Answers, topics: CurriculumTopic[]): CurriculumItem[] {
  const refs = padToMinimum(practicalCourses(a, false, false), a)
  const items = toItems(refs, topics, 1)
  items.push({
    order: items.length + 1,
    name: inhouseSupportName(a),
    aim: '貴社のアカウントを見ながら、改善の判断を一緒に繰り返す',
    count: null,
    countLabel: '継続',
  })
  return items
}

// 型Dの共通土台（全員が受講する行）の回数。
const TYPE_D_COMMON_BLOCK_COUNT = 4

function buildTypeD(a: Answers, topics: CurriculumTopic[]): CurriculumItem[] {
  const items: CurriculumItem[] = []
  items.push({
    order: 1,
    name: `${courseTitle(topics, 'marketing-overview')}／${courseTitle(topics, 'digital-marketing-strategy')}／${courseTitle(topics, 'ga4')}`,
    aim: '役割によらず共通で持つべき、全体像とKPI・数値の共通言語をつくる（対象：全員）',
    count: TYPE_D_COMMON_BLOCK_COUNT,
  })

  const nonExecRoles = a.Q11.filter((v) => v !== 'q11_exec')
  if (nonExecRoles.length > 0 || a.Q11.length === 0) {
    const practical = practicalCourses(a, true, true)
    const refs = practical.length > 0 ? practical : [{ urlName: 'listing', count: 3 }]
    items.push(...toItems(refs, topics, items.length + 1))
  } else if (MIN_SESSIONS - TYPE_D_COMMON_BLOCK_COUNT > 0) {
    // 対象者が経営・管理職のみの場合、実務講座は対象外という設計だが、
    // 最低5回ルール（助成金の訓練時間下限）は型によらず一律のため満たす必要がある。
    // 共通土台に含まれるGA4基礎に続く「GA4応用」を追加する（不足分は必ずMIN_SESSIONS -
    // TYPE_D_COMMON_BLOCK_COUNT = 1回のため、2回講座のGA4応用を足せば必ず満たせる）。
    items.push({
      order: items.length + 1,
      name: courseTitle(topics, 'ga4-advanced'),
      aim: courseAim('ga4-advanced'),
      count: 2,
      urlName: 'ga4-advanced',
    })
  }

  if (a.Q11.includes('q11_exec')) {
    items.push({
      order: items.length + 1,
      name: `${courseTitle(topics, 'marketing-overview')}／${courseTitle(topics, 'digital-marketing-strategy')}（共通土台のみ受講）`,
      aim: '意思決定に必要な全体像とKPI設計の共通言語を持つ（対象：経営・管理職）',
      count: null,
      countLabel: '共通土台のみ',
    })
  }

  return items
}

export function buildCurriculum(a: Answers, type: ProposalType, topics: CurriculumTopic[]): CurriculumItem[] {
  switch (type) {
    case 'A':
      return buildTypeA(a, topics)
    case 'B':
      return buildTypeB(a, topics)
    case 'C':
      return buildTypeC(a, topics)
    case 'D':
      return buildTypeD(a, topics)
  }
}

export function sumSessions(curriculum: CurriculumItem[]): number {
  return curriculum.reduce((sum, item) => sum + (typeof item.count === 'number' ? item.count : 0), 0)
}

// §6.4: 最小5回ルールにより5未満は発生しない想定。下回った場合は呼び出し側（judge）でエラーとする。
export function toDuration(totalSessions: number): string {
  if (totalSessions <= 6) return '約1.5ヶ月'
  if (totalSessions <= 8) return '約2ヶ月'
  if (totalSessions <= 10) return '約2.5ヶ月'
  return '約3ヶ月'
}

export function durationLabel(totalSessions: number): string {
  const duration = toDuration(totalSessions)
  return `全${totalSessions}回・${duration}（週1回ペースの場合）`
}
